import { useState } from 'react';

const trades = [
  'Electrician',
  'Plumber',
  'HVAC',
  'General Contractor',
  'Roofer',
  'Painter',
  'Carpenter',
  'Landscaper',
  'Mason',
  'Flooring',
  'Other'
];

const experienceLevels = [
  'Less than 1 year',
  '1-2 years',
  '3-5 years',
  '6-10 years',
  '10+ years'
];

const features = [
  { id: 'estimates', label: 'Estimates & Proposals' },
  { id: 'invoicing', label: 'Invoicing & Payments' },
  { id: 'scheduling', label: 'Job Scheduling' },
  { id: 'crm', label: 'Customer Management (CRM)' },
  { id: 'infocenter', label: 'Info Center & Guidance' },
  { id: 'expenses', label: 'Expense Tracking' },
  { id: 'inventory', label: 'Inventory Management' },
  { id: 'reporting', label: 'Reports & Analytics' }
];

const regions = [
  'Northeast (NY, NJ, PA, CT, MA, etc.)',
  'Southeast (FL, GA, NC, SC, VA, etc.)',
  'Midwest (IL, OH, MI, IN, WI, etc.)',
  'Southwest (TX, AZ, NM, OK, etc.)',
  'West (CA, WA, OR, NV, CO, etc.)',
  'Multiple Regions'
];

export default function Waitlist() {
  const [formData, setFormData] = useState({
    email: '',
    trade: '',
    otherTrade: '',
    experience: '',
    selectedFeatures: [] as string[],
    region: '',
    veteranOwned: false
  });
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');

  const handleFeatureToggle = (featureId: string) => {
    setFormData(prev => ({
      ...prev,
      selectedFeatures: prev.selectedFeatures.includes(featureId)
        ? prev.selectedFeatures.filter(f => f !== featureId)
        : [...prev.selectedFeatures, featureId]
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.email || !formData.email.includes('@')) {
      setStatus('error');
      setMessage('Please enter a valid email address');
      return;
    }

    if (!formData.trade) {
      setStatus('error');
      setMessage('Please select your trade');
      return;
    }

    if (!formData.experience) {
      setStatus('error');
      setMessage('Please select your experience level');
      return;
    }

    if (!formData.region) {
      setStatus('error');
      setMessage('Please select your service region');
      return;
    }

    setStatus('loading');

    try {
      const submitData = new FormData();
      submitData.append('email', formData.email);
      submitData.append('trade', formData.trade === 'Other' && formData.otherTrade 
        ? `Other: ${formData.otherTrade}` 
        : formData.trade);
      submitData.append('experience', formData.experience);
      submitData.append('features', formData.selectedFeatures.map(id => 
        features.find(f => f.id === id)?.label || id
      ).join(', '));
      submitData.append('region', formData.region);
      submitData.append('veteranOwned', formData.veteranOwned ? 'Yes' : 'No');

      const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
      const response = await fetch(`${supabaseUrl}/functions/v1/waitlist-submit`, {
        method: 'POST',
        body: submitData
      });

      const responseData = await response.json();

      if (response.ok && responseData.success) {
        setStatus('success');
        setMessage('You\'re on the list! Check your email for confirmation.');
        setFormData({
          email: '',
          trade: '',
          otherTrade: '',
          experience: '',
          selectedFeatures: [],
          region: '',
          veteranOwned: false
        });
      } else {
        setStatus('error');
        setMessage(responseData.error || 'Something went wrong. Please try again.');
        console.error('Submission error:', responseData);
      }
    } catch (error) {
      setStatus('error');
      setMessage('Network error. Please check your connection and try again.');
      console.error('Form submission error:', error);
    }
  };

  return (
    <section id="waitlist" className="bg-[#1a2332] py-28">
      <div className="max-w-4xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-12">
          <span className="inline-block px-4 py-2 bg-white/20 text-white rounded-full text-xs font-semibold uppercase tracking-wider mb-6">
            Early Access
          </span>
          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight">
            Be first in line when we launch
          </h2>
          <p className="text-lg text-gray-300 leading-relaxed max-w-2xl mx-auto">
            Tell us about your business so we can tailor AtlasIQ to your needs. Get exclusive early access and special pricing.
          </p>
        </div>

        {/* Form */}
        <form 
          id="waitlist-extended-form"
          data-readdy-form
          onSubmit={handleSubmit} 
          className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10"
        >
          {/* Email */}
          <div className="mb-8">
            <label className="block text-white font-medium mb-3">
              Email Address <span className="text-[#ff6b35]">*</span>
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
              placeholder="your@email.com"
              className="w-full h-12 px-5 rounded-lg bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#ff6b35] focus:border-transparent transition-all"
              disabled={status === 'loading'}
              required
            />
          </div>

          {/* Trade & Experience Row */}
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            {/* Trade */}
            <div>
              <label className="block text-white font-medium mb-3">
                What's your trade? <span className="text-[#ff6b35]">*</span>
              </label>
              <select
                name="trade"
                value={formData.trade}
                onChange={(e) => setFormData(prev => ({ ...prev, trade: e.target.value, otherTrade: e.target.value !== 'Other' ? '' : prev.otherTrade }))}
                className="w-full h-12 px-5 rounded-lg bg-white/10 border border-white/20 text-white focus:outline-none focus:ring-2 focus:ring-[#ff6b35] focus:border-transparent transition-all cursor-pointer appearance-none"
                style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='white'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center', backgroundSize: '20px' }}
                disabled={status === 'loading'}
                required
              >
                <option value="" className="bg-[#1a2332]">Select your trade</option>
                {trades.map(trade => (
                  <option key={trade} value={trade} className="bg-[#1a2332]">{trade}</option>
                ))}
              </select>
              
              {/* Other Trade Text Field */}
              {formData.trade === 'Other' && (
                <input
                  type="text"
                  name="otherTrade"
                  value={formData.otherTrade}
                  onChange={(e) => setFormData(prev => ({ ...prev, otherTrade: e.target.value }))}
                  placeholder="Please specify your trade"
                  className="w-full h-12 px-5 mt-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#ff6b35] focus:border-transparent transition-all"
                  disabled={status === 'loading'}
                  maxLength={100}
                />
              )}
            </div>

            {/* Experience */}
            <div>
              <label className="block text-white font-medium mb-3">
                How long in business? <span className="text-[#ff6b35]">*</span>
              </label>
              <select
                name="experience"
                value={formData.experience}
                onChange={(e) => setFormData(prev => ({ ...prev, experience: e.target.value }))}
                className="w-full h-12 px-5 rounded-lg bg-white/10 border border-white/20 text-white focus:outline-none focus:ring-2 focus:ring-[#ff6b35] focus:border-transparent transition-all cursor-pointer appearance-none"
                style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='white'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center', backgroundSize: '20px' }}
                disabled={status === 'loading'}
                required
              >
                <option value="" className="bg-[#1a2332]">Select experience</option>
                {experienceLevels.map(level => (
                  <option key={level} value={level} className="bg-[#1a2332]">{level}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Features */}
          <div className="mb-8">
            <label className="block text-white font-medium mb-3">
              Which features interest you most?
            </label>
            <p className="text-gray-400 text-sm mb-4">Select all that apply</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {features.map(feature => (
                <label
                  key={feature.id}
                  className={`flex items-center gap-3 p-4 rounded-lg border cursor-pointer transition-all ${
                    formData.selectedFeatures.includes(feature.id)
                      ? 'bg-[#ff6b35]/20 border-[#ff6b35] text-white'
                      : 'bg-white/5 border-white/10 text-gray-300 hover:border-white/30'
                  }`}
                >
                  <input
                    type="checkbox"
                    name={`feature_${feature.id}`}
                    checked={formData.selectedFeatures.includes(feature.id)}
                    onChange={() => handleFeatureToggle(feature.id)}
                    className="sr-only"
                    disabled={status === 'loading'}
                  />
                  <span className={`w-5 h-5 flex items-center justify-center rounded border-2 transition-all ${
                    formData.selectedFeatures.includes(feature.id)
                      ? 'bg-[#ff6b35] border-[#ff6b35]'
                      : 'border-gray-500'
                  }`}>
                    {formData.selectedFeatures.includes(feature.id) && (
                      <i className="ri-check-line text-white text-sm"></i>
                    )}
                  </span>
                  <span className="text-sm">{feature.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Region */}
          <div className="mb-8">
            <label className="block text-white font-medium mb-3">
              Where do you operate? <span className="text-[#ff6b35]">*</span>
            </label>
            <select
              name="region"
              value={formData.region}
              onChange={(e) => setFormData(prev => ({ ...prev, region: e.target.value }))}
              className="w-full h-12 px-5 rounded-lg bg-white/10 border border-white/20 text-white focus:outline-none focus:ring-2 focus:ring-[#ff6b35] focus:border-transparent transition-all cursor-pointer appearance-none"
              style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='white'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center', backgroundSize: '20px' }}
              disabled={status === 'loading'}
              required
            >
              <option value="" className="bg-[#1a2332]">Select your region</option>
              {regions.map(region => (
                <option key={region} value={region} className="bg-[#1a2332]">{region}</option>
              ))}
            </select>
          </div>

          {/* Veteran Owned Checkbox */}
          <div className="mb-8">
            <label className="flex items-start gap-3 p-5 rounded-lg border border-white/10 bg-white/5 cursor-pointer hover:border-[#ff6b35]/50 transition-all">
              <input
                type="checkbox"
                name="veteranOwned"
                checked={formData.veteranOwned}
                onChange={(e) => setFormData(prev => ({ ...prev, veteranOwned: e.target.checked }))}
                className="sr-only"
                disabled={status === 'loading'}
              />
              <span className={`w-5 h-5 flex items-center justify-center rounded border-2 transition-all flex-shrink-0 mt-0.5 ${
                formData.veteranOwned
                  ? 'bg-[#ff6b35] border-[#ff6b35]'
                  : 'border-gray-500'
              }`}>
                {formData.veteranOwned && (
                  <i className="ri-check-line text-white text-sm"></i>
                )}
              </span>
              <div>
                <span className="text-white font-medium block mb-1">
                  Veteran-Owned Business
                </span>
                <span className="text-gray-400 text-sm">
                  We offer special discounts for veteran-owned businesses 🇺🇸
                </span>
              </div>
            </label>
          </div>

          {/* Status Message */}
          {status === 'success' && (
            <div className="mb-6 p-4 bg-green-500/20 border border-green-500/50 rounded-lg text-green-200 text-center">
              <i className="ri-checkbox-circle-fill text-xl mr-2"></i>
              {message}
            </div>
          )}
          {status === 'error' && (
            <div className="mb-6 p-4 bg-red-500/20 border border-red-500/50 rounded-lg text-red-200 text-center">
              <i className="ri-error-warning-fill text-xl mr-2"></i>
              {message}
            </div>
          )}

          {/* Submit */}
          <button
            type="submit"
            disabled={status === 'loading'}
            className="w-full h-14 bg-[#ff6b35] text-white font-semibold rounded-lg hover:bg-[#ff5722] transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer text-lg"
          >
            {status === 'loading' ? (
              <span className="flex items-center justify-center gap-2">
                <i className="ri-loader-4-line animate-spin"></i>
                Joining...
              </span>
            ) : (
              'Join the Waitlist'
            )}
          </button>

          {/* Trust Elements */}
          <div className="flex flex-wrap justify-center items-center gap-4 text-sm text-gray-400 mt-6">
            <span className="flex items-center gap-2">
              <i className="ri-shield-check-line text-green-400"></i>
              No spam, ever
            </span>
            <span className="text-gray-600">•</span>
            <span className="flex items-center gap-2">
              <i className="ri-gift-line text-[#ff6b35]"></i>
              Early adopter pricing
            </span>
            <span className="text-gray-600">•</span>
            <span className="flex items-center gap-2">
              <i className="ri-user-voice-line text-[#ff6b35]"></i>
              Help shape the product
            </span>
          </div>
        </form>
      </div>
    </section>
  );
}
